import Build from "@mui/icons-material/Build";
import { Box, Divider, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, CONTENT_WIDTH, SHADOW } from "@/theme/tokens";
import { EmptyState, EmptyStateSize } from "@/ui/molecules/EmptyState";
import { FocalCard } from "@/ui/molecules/FocalCard";
import { SigninLogo } from "../SigninLogo/SigninLogo";
import { MaintenanceFootnotes } from "./MaintenanceFootnotes";
import { MaintenanceStatusPanel } from "./MaintenanceStatusPanel";

type Props = { endsAt: DateTime | null };

/** The maintenance screen's content; `MaintenanceScreen` wraps it in the theme it needs. */
export const MaintenanceScreenBody: FC<Props> = ({ endsAt }) => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const direction = i18n.dir();

	useLayoutEffect(() => {
		document.body.dir = direction;
	}, [direction]);

	return (
		<Box
			component="main"
			data-testid="maintenance-screen-root"
			sx={{
				minHeight: "100dvh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: { xs: 3, sm: 4 },
				px: 2,
				// Clear the notch and home indicator in the native app.
				pt: `calc(${theme.spacing(4)} + env(safe-area-inset-top, 0px))`,
				pb: `calc(${theme.spacing(4)} + env(safe-area-inset-bottom, 0px))`,
			}}
		>
			<SigninLogo />
			<FocalCard ink={theme.palette.primary.main} sx={{ width: "100%", maxWidth: CONTENT_WIDTH.SCREEN_CARD, boxShadow: SHADOW.FLOATING_WINDOW }}>
				<EmptyState
					size={EmptyStateSize.Page}
					titleComponent="h1"
					icon={<Build aria-hidden />}
					// The screen's one focal mark: a notch deeper than a panel tile, so it holds against the card's wash.
					iconColor={theme.palette.primary.dark}
					iconTint={alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER)}
					title={t("maintenance.planned.title")}
					hint={t("maintenance.planned.body")}
					dataTestId="maintenance-screen"
					sx={{ height: "auto" }}
				>
					<MaintenanceStatusPanel endsAt={endsAt} autoReload={t("maintenance.planned.auto.reload")} />
				</EmptyState>
				<Divider />
				<MaintenanceFootnotes />
			</FocalCard>
		</Box>
	);
};
